import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui";
import type {
  ChangelogChannel,
  ChangelogRecipientsPage,
} from "@/features/changelog/api/changelog.api";
import {
  findDelivery,
  formatDeployedAt,
  isChannelInFlight,
  isChannelSent,
  type ChangelogSendable,
} from "@/features/changelog/lib/changelog-display";
import {
  VendorEmailFrame,
  type VendorEmailViewMode,
} from "@/features/marketplace/components/vendor-email/VendorEmailFrame";
import { changelogDigestEmailPreviewQueryOptions } from "@/lib/queries";
import { CHANNEL_ICON } from "./shared/ChangelogDeliveryList";

/** Why a channel cannot be picked; `null` means it can. Doubles as an i18n key. */
type BlockedReason =
  | "alreadySent"
  | "inFlight"
  | "slackUnconfigured"
  | "noRecipients"
  | "inAppOnHold"
  | "inAppBigNewsOnly"
  | null;

/** Why in-app is refused before the API is asked — null means it can go out. */
export type ChangelogInAppBlockedReason = "inAppOnHold" | "inAppBigNewsOnly";

interface ChannelState {
  channel: ChangelogChannel;
  blockedReason: BlockedReason;
}

/**
 * Mirrors the refusals in the send routes. While the mailing list is still
 * loading, Slack and email are left pickable rather than guessed at: the API is
 * the authority, and a channel that flickers from available to blocked is worse
 * than one that answers with an error.
 */
function blockedReasonFor(
  channel: ChangelogChannel,
  target: ChangelogSendable,
  recipients: ChangelogRecipientsPage | undefined,
  activeRecipientCount: number,
  inAppBlockedReason: ChangelogInAppBlockedReason | null,
): BlockedReason {
  if (isChannelSent(target, channel)) return "alreadySent";
  // The API treats anything but FAILED as taken, so offering a mid-flight
  // channel would only earn a 409 that reads as "already sent" when it is not.
  if (isChannelInFlight(target, channel)) return "inFlight";
  if (channel === "SLACK" && recipients && !recipients.slackConfigured) {
    return "slackUnconfigured";
  }
  if (channel === "EMAIL" && recipients && activeRecipientCount === 0) {
    return "noRecipients";
  }
  if (channel === "IN_APP" && inAppBlockedReason !== null) {
    return inAppBlockedReason;
  }
  return null;
}

function RoundupEmailPreview({
  preview,
  isPending,
  isError,
}: {
  preview: {
    from: string;
    to: string;
    recipientCount: number;
    subject: string;
    html: string;
    text: string;
  } | undefined;
  isPending: boolean;
  isError: boolean;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<VendorEmailViewMode>("desktop");

  return (
    <div className="flex h-[28rem] min-h-0 flex-col gap-2">
      {isPending ? (
        <p className="m-0 text-xs text-muted-foreground">
          {t("admin.changelog.send.emailPreviewLoading", "Rendering the roundup email…")}
        </p>
      ) : null}
      {isError ? (
        <p
          role="alert"
          className="m-0 text-xs text-error-subtle-foreground"
        >
          {t("admin.changelog.send.emailPreviewFailed", "Couldn't render the roundup email.")}
        </p>
      ) : null}
      {preview ? (
        <VendorEmailFrame
          from={preview.from}
          replyTo={null}
          to={preview.to}
          toSummary={
            preview.to
              ? null
              : t("admin.changelog.send.recipientCount", { count: preview.recipientCount })
          }
          cc=""
          subject={preview.subject}
          html={preview.html}
          text={preview.text}
          view={view}
          onViewChange={setView}
          iframeTitle={t("admin.changelog.send.emailPreviewTitle", "Roundup email preview")}
        />
      ) : null}
    </div>
  );
}

interface ChangelogSendDialogProps {
  /** Stable across the polling that happens while a send is in flight. */
  targetId: string;
  /**
   * Set for a roundup. When email is ticked, the dialog shows the same mail
   * preview used before a vendor handoff.
   */
  digestId?: string;
  /** When false, Email can still send but the preview is not fetched. */
  canPreviewDigestEmail?: boolean;
  title: string;
  target: ChangelogSendable;
  /** Which channels this unit may use at all — a deploy's differ from a roundup's. */
  channels: ChangelogChannel[];
  isOpen: boolean;
  isSending: boolean;
  sendError: string | null;
  /** Undefined while the mailing list loads, or when the admin cannot read it. */
  recipients: ChangelogRecipientsPage | undefined;
  /**
   * Why in-app cannot go out under the "what users see" setting. The API
   * refuses the channel either way; this makes the dialog say so first.
   */
  inAppBlockedReason?: ChangelogInAppBlockedReason | null;
  /** Omitted where the mailing list is beside the point, as it is for a deploy. */
  onManageRecipients?: () => void;
  onOpenChange: (open: boolean) => void;
  onSend: (channels: ChangelogChannel[]) => void;
}

/**
 * The last stop before the notes leave the building: which channels, and what
 * stands in the way of each. The notes themselves are not repeated here — the
 * detail sheet that opened this is still behind it, showing exactly what will
 * go out. Channels that cannot work stay visible but disabled with the reason
 * attached, since a greyed row that explains itself beats one that vanished.
 */
export function ChangelogSendDialog({
  targetId,
  digestId,
  canPreviewDigestEmail = false,
  title,
  target,
  channels: available,
  isOpen,
  isSending,
  sendError,
  recipients,
  inAppBlockedReason = null,
  onManageRecipients,
  onOpenChange,
  onSend,
}: ChangelogSendDialogProps) {
  const { t, i18n } = useTranslation();

  function alreadySentHint(channel: ChangelogChannel): string {
    const delivery = findDelivery(target, channel);
    const at = delivery?.sentAt ? formatDeployedAt(delivery.sentAt, i18n.language) : null;
    const by = delivery?.requestedByName ?? null;

    if (at && by) {
      return t("admin.changelog.send.alreadySentByAt", {
        defaultValue: "Sent {{at}} by {{by}}",
        at,
        by,
      });
    }
    if (at) {
      return t("admin.changelog.send.alreadySentAt", { defaultValue: "Sent {{at}}", at });
    }
    if (by) {
      return t("admin.changelog.send.alreadySentBy", { defaultValue: "Sent by {{by}}", by });
    }
    return t("admin.changelog.send.alreadySent", "Already sent");
  }

  const activeRecipientCount =
    recipients?.recipients.filter((recipient) => recipient.isActive).length ?? 0;

  // Availability only, with no copy in it: the strings would drag `t` into the
  // dependencies, and a new `t` identity mid-dialog would reset the admin's
  // ticks under them.
  const channels = useMemo<ChannelState[]>(
    () =>
      available.map((channel) => ({
        channel,
        blockedReason: blockedReasonFor(
          channel,
          target,
          recipients,
          activeRecipientCount,
          inAppBlockedReason,
        ),
      })),
    [available, target, recipients, activeRecipientCount, inAppBlockedReason],
  );

  const hasSendableChannel = channels.some(({ blockedReason }) => blockedReason === null);
  const sendableChannels = useMemo(
    () => new Set(channels.filter(({ blockedReason }) => blockedReason === null).map(({ channel }) => channel)),
    [channels],
  );

  const [selected, setSelected] = useState<ChangelogChannel[]>([]);
  // Drop channels that became blocked after open (e.g. Slack once recipients load)
  // without re-ticking ones the admin cleared.
  const selectedSendable = selected.filter((channel) => sendableChannels.has(channel));

  const showEmailPreview =
    Boolean(digestId) && canPreviewDigestEmail && selectedSendable.includes("EMAIL");

  const previewQuery = useQuery({
    ...changelogDigestEmailPreviewQueryOptions(digestId ?? ""),
    enabled: showEmailPreview && Boolean(digestId),
  });
  const previewData = previewQuery.isError ? undefined : previewQuery.data;
  const effectiveRecipientCount =
    recipients !== undefined
      ? activeRecipientCount
      : (previewData?.recipientCount ?? activeRecipientCount);

  // Everything sendable starts ticked, recomputed each time the dialog opens so
  // a reopen after a partial send does not re-offer what already went out.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(
      available.filter(
        (channel) =>
          blockedReasonFor(
            channel,
            target,
            recipients,
            activeRecipientCount,
            inAppBlockedReason,
          ) === null,
      ),
    );
    // Deliberately keyed on the id rather than the target object: the list polls
    // while a send is in flight, and a fresh object each poll would keep
    // re-ticking boxes the admin had just cleared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, targetId]);

  // Recipients (and similar gates) arrive after open. A channel that was ticked
  // while unknown and then blocked must leave the selection — otherwise it
  // stays checked and disabled, and Send still tries to post it.
  useEffect(() => {
    if (!isOpen) return;
    setSelected((current) => {
      const next = current.filter((channel) => sendableChannels.has(channel));
      return next.length === current.length ? current : next;
    });
  }, [isOpen, sendableChannels]);

  function toggle(channel: ChangelogChannel) {
    setSelected((current) =>
      current.includes(channel)
        ? current.filter((value) => value !== channel)
        : [...current, channel],
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        variant={showEmailPreview ? "form" : "confirmation"}
        className="max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t(
              "admin.changelog.send.description",
              "Delivery cannot be undone for a channel once it has gone out.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {channels.map(({ channel, blockedReason }) => (
            <label
              key={channel}
              className={cn(
                "flex items-start gap-3 rounded-md border border-border p-3 transition-colors",
                blockedReason === null
                  ? "cursor-pointer hover:border-input-focus-border"
                  : "cursor-not-allowed opacity-60",
              )}
            >
              <Checkbox
                className="mt-0.5"
                checked={selectedSendable.includes(channel)}
                disabled={blockedReason !== null || isSending}
                onCheckedChange={() => toggle(channel)}
              />
              <Icon name={CHANNEL_ICON[channel]} size="sm" className="mt-0.5" aria-hidden />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-xs text-foreground">
                  {t(`admin.changelog.channels.${channel}`, { defaultValue: channel })}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {blockedReason === "alreadySent"
                    ? alreadySentHint(channel)
                    : blockedReason !== null
                      ? t(`admin.changelog.send.${blockedReason}`)
                      : channel === "EMAIL"
                        ? t("admin.changelog.send.recipientCount", {
                            count: effectiveRecipientCount,
                          })
                        : t(`admin.changelog.channelHints.${channel}`, { defaultValue: "" })}
                </span>
              </span>
              {channel === "EMAIL" && onManageRecipients ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.preventDefault();
                    onManageRecipients();
                  }}
                >
                  {t("admin.changelog.send.manageList", "Manage list")}
                </Button>
              ) : null}
            </label>
          ))}
        </div>

        {!hasSendableChannel ? (
          <p className="m-0 text-[11px] text-muted-foreground">
            {t(
              "admin.changelog.send.nothingSendable",
              "No channel can take these notes right now.",
            )}
          </p>
        ) : null}

        {showEmailPreview ? (
          <RoundupEmailPreview
            preview={previewData}
            isPending={previewQuery.isPending}
            isError={previewQuery.isError}
          />
        ) : null}

        {sendError ? (
          <div className="rounded-md border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
            {sendError}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" size="sm" disabled={isSending} onClick={() => onOpenChange(false)}>
            {hasSendableChannel
              ? t("admin.changelog.send.cancel", "Cancel")
              : t("admin.changelog.send.close", "Close")}
          </Button>
          {/* Offering a send with nothing to send reads as a broken dialog rather
              than a blocked one, so the button goes when no channel can take it. */}
          {hasSendableChannel ? (
            <Button
              size="sm"
              disabled={isSending || selectedSendable.length === 0}
              onClick={() => onSend(selectedSendable)}
            >
              {isSending
                ? t("admin.changelog.send.sending", "Sending…")
                : t("admin.changelog.send.confirm", { count: selectedSendable.length })}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
